'use client';

import React, { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import useSWR from 'swr';
import { getTeamChannel, identifyWS } from '@/lib/ws-client';
import { useFloatingChat } from '@/components/chat/FloatingChatContext';
import { Button } from '@/components/ui/button';
import { MessageSquare, Pin, X, CheckCircle2, User, Phone, Sparkles, Volume2, Flame, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { playAlertRing, getSelectedSound, getAlertRingEnabled, NOTIFICATION_SOUNDS } from '@/lib/sound';
import { handleIncomingMessageAlert, initAudioUnlock, registerNotificationServiceWorker } from '@/lib/browser-notifications';

interface AlertLead {
  chatId: number;
  contactId?: number;
  name: string;
  remoteJid: string;
  instanceId?: number;
  text: string;
  timestamp: string;
  avatar?: string | null;
  claimedBy?: string;
  claiming?: boolean;
  isBidding?: boolean;
  biddingEndsAt?: string;
  biddingWindowSeconds?: number;
  participants?: { userId: number; userName: string }[];
  myBidSubmitted?: boolean;
  isWinnerMe?: boolean;
}

const fetcher = (url: string) => fetch(url).then((res) => res.json());

export function NewChatAlertListener() {
  const pathname = usePathname();
  const { openChat, dockedChats } = useFloatingChat();
  const { data: team } = useSWR('/api/team', fetcher);
  const teamId = team?.id;
  const { data: membership } = useSWR('/api/team/membership', fetcher);
  const membershipRef = useRef<any>(null);
  membershipRef.current = membership;

  // Ensure WebSocket is authenticated with current user session
  useEffect(() => {
    if (membership?.userId) {
      identifyWS({
        userId: membership.userId,
        teamId: membership.teamId,
        role: membership.role,
        canSeeAllChats: membership.role === 'owner' || membership.permissions?.chatVisibility === 'all',
      });
    }
  }, [membership]);

  const [selectedSound, setSelectedSound] = useState('sound1');
  const [alerts, setAlerts] = useState<AlertLead[]>([]);
  const timeoutsRef = useRef<Record<number, NodeJS.Timeout>>({});
  const dockedChatsRef = useRef(dockedChats);
  dockedChatsRef.current = dockedChats;

  useEffect(() => {
    initAudioUnlock();
    registerNotificationServiceWorker();
  }, []);

  const dismissTimersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  const pathnameWithoutLocale = pathname ? pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') : '';

  // Track currently selected sound and respond reactively to preference changes
  useEffect(() => {
    setSelectedSound(getSelectedSound());

    const handleSoundChange = (e: any) => {
      if (e?.detail) {
        setSelectedSound(String(e.detail));
      } else {
        setSelectedSound(getSelectedSound());
      }
    };

    window.addEventListener('selectedSoundChange', handleSoundChange);
    return () => {
      window.removeEventListener('selectedSoundChange', handleSoundChange);
    };
  }, []);

  // Unlock browser audio context on first user interaction so ring tone is never blocked
  useEffect(() => {
    const unlockAudio = () => {
      try {
        const audio = new Audio('/sounds/notification_1.mp3');
        audio.volume = 0.001;
        const p = audio.play();
        if (p) {
          p.then(() => {
            audio.pause();
            audio.currentTime = 0;
          }).catch(() => {});
        }
      } catch {}
    };

    window.addEventListener('click', unlockAudio, { once: true, passive: true });
    window.addEventListener('keydown', unlockAudio, { once: true, passive: true });
    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Auto-dismiss helper with 12-second lifecycle
  const scheduleDismiss = (chatId: number, durationMs = 12000) => {
    if (dismissTimersRef.current.has(chatId)) {
      clearTimeout(dismissTimersRef.current.get(chatId)!);
    }
    const timer = setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.chatId !== chatId));
      dismissTimersRef.current.delete(chatId);
    }, durationMs);
    dismissTimersRef.current.set(chatId, timer);
  };

  const cancelDismiss = (chatId: number) => {
    if (dismissTimersRef.current.has(chatId)) {
      clearTimeout(dismissTimersRef.current.get(chatId)!);
      dismissTimersRef.current.delete(chatId);
    }
  };

  // Clean up timers on unmount
  useEffect(() => {
    const timers = dismissTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  // Unified incoming message handler
  const handleIncomingAlert = (payload: {
    chatId: number;
    contactId?: number;
    name?: string;
    remoteJid: string;
    instanceId?: number;
    text?: string;
    timestamp?: string;
    avatar?: string | null;
    claimedBy?: string;
    isBidding?: boolean;
    biddingEndsAt?: string;
    biddingWindowSeconds?: number;
    participants?: { userId: number; userName: string }[];
  }) => {
    if (!payload.remoteJid || !payload.chatId) return;

    // 0. Only suppress if the agent is currently viewing this exact chat in full screen
    const isCurrentlyViewingThisChat =
      pathnameWithoutLocale.startsWith('/dashboard/chat/') &&
      ((typeof window !== 'undefined' && (
        (window as any).__activeChatId === payload.chatId ||
        (payload.remoteJid && (window as any).__activeRemoteJid === payload.remoteJid)
      )) ||
        pathnameWithoutLocale.includes(payload.remoteJid) ||
        (payload.remoteJid && pathnameWithoutLocale.includes(payload.remoteJid.split('@')[0])));

    if (isCurrentlyViewingThisChat) {
      return;
    }

    // 1. If chat is already open & unminimized in the bottom floating dock, suppress alert
    const isAlreadyOpenInDock = dockedChatsRef.current.some(
      (c) => !c.isMinimized && (c.id === payload.chatId || c.remoteJid === payload.remoteJid)
    );
    if (isAlreadyOpenInDock) {
      return;
    }

    // 2. Play the user's selected audible ringtone
    playAlertRing(selectedSound);

    const senderName =
      payload.name ||
      payload.remoteJid.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('@g.us', '').replace('@web.chat', '');
    const messageSnippet = payload.text || 'New message received';
    const timeIso = payload.timestamp || new Date().toISOString();

    // 3. Update or append alert in right-side queue
    setAlerts((prev) => {
      const existsIndex = prev.findIndex(
        (a) => a.chatId === payload.chatId || a.remoteJid === payload.remoteJid
      );

      if (existsIndex > -1) {
        const updated = [...prev];
        updated[existsIndex] = {
          ...updated[existsIndex],
          contactId: payload.contactId || updated[existsIndex].contactId,
          name: senderName || updated[existsIndex].name,
          text: messageSnippet,
          timestamp: timeIso,
          avatar: payload.avatar || updated[existsIndex].avatar,
          instanceId: payload.instanceId ?? updated[existsIndex].instanceId,
          claimedBy: payload.claimedBy || updated[existsIndex].claimedBy,
          isBidding: payload.isBidding !== undefined ? payload.isBidding : updated[existsIndex].isBidding,
          biddingEndsAt: payload.biddingEndsAt || updated[existsIndex].biddingEndsAt,
          biddingWindowSeconds: payload.biddingWindowSeconds || updated[existsIndex].biddingWindowSeconds,
          participants: payload.participants || updated[existsIndex].participants,
        };
        return updated;
      }

      // Keep at most 3 active alert cards on screen
      return [
        {
          chatId: payload.chatId,
          contactId: payload.contactId,
          name: senderName,
          remoteJid: payload.remoteJid,
          instanceId: payload.instanceId,
          text: messageSnippet,
          timestamp: timeIso,
          avatar: payload.avatar,
          claimedBy: payload.claimedBy,
          isBidding: payload.isBidding,
          biddingEndsAt: payload.biddingEndsAt,
          biddingWindowSeconds: payload.biddingWindowSeconds,
          participants: payload.participants,
        },
        ...prev.slice(0, 2),
      ];
    });

    scheduleDismiss(payload.chatId, payload.isBidding ? ((payload.biddingWindowSeconds || 10) + 4) * 1000 : 15000);
  };

  useEffect(() => {
    if (!teamId) return;

    const channel = getTeamChannel(teamId);
    if (!channel) return;

    const checkPrivacy = (payload: any) => {
      const mem = membershipRef.current;
      if (!mem?.userId) return false;
      const isPrivileged = mem.role === 'owner' || (mem.role !== 'agent' && mem.permissions?.chatVisibility === 'all');
      if (isPrivileged) return true;

      // STRICT AGENT PRIVACY:
      // If payload has an assigned agent and it is NOT me -> STRICTLY DROP
      if (payload?.assignedUserId && payload.assignedUserId !== mem.userId) {
        return false;
      }
      // If payload is assigned to me -> ALLOW
      if (payload?.assignedUserId && payload.assignedUserId === mem.userId) {
        return true;
      }
      // If customer is unassigned: only ring on genuine first-time customer lead inquiries
      if (!payload?.assignedUserId && (payload?.isFirstMessage === true || payload?.event === 'new-lead-alert')) {
        return true;
      }
      // All other messages for unassigned/foreign chats -> DROP
      return false;
    };

    // Listener 1: High priority new unassigned lead alert or first-time message
    const handleNewLeadAlert = (payload: any) => {
      if (!payload?.chatId || !checkPrivacy(payload)) return;

      handleIncomingMessageAlert({
        chatId: payload.chatId,
        senderName: payload.name || payload.pushName || 'New WhatsApp Lead',
        messageText: payload.text || 'New customer inquiry',
        avatarUrl: payload.avatar,
        remoteJid: payload.remoteJid,
        instanceId: payload.instanceId,
        soundId: selectedSound,
      });

      handleIncomingAlert({
        chatId: payload.chatId,
        contactId: payload.contactId,
        name: payload.name || payload.pushName,
        remoteJid: payload.remoteJid,
        instanceId: payload.instanceId,
        text: payload.text,
        timestamp: payload.timestamp,
        avatar: payload.avatar,
        claimedBy: payload.claimedBy,
      });
    };

    // Listener 2: Incoming message event (trigger quick alert on customer messages)
    const handleNewMessage = (payload: any) => {
      if (!payload?.chatId || payload?.fromMe === true || !checkPrivacy(payload)) return;

      handleIncomingMessageAlert({
        chatId: payload.chatId,
        senderName: payload.name || payload.pushName || payload.participantName || payload.remoteJid?.split('@')[0],
        messageText: payload.lastMessageTextPreview || payload.text || 'New message received',
        avatarUrl: payload.avatar || payload.profilePicUrl,
        remoteJid: payload.remoteJid,
        instanceId: payload.instanceId,
        soundId: selectedSound,
      });

      if (payload?.isFirstMessage) {
        handleIncomingAlert({
          chatId: payload.chatId,
          contactId: payload.contactId,
          name: payload.name || payload.pushName || payload.participantName,
          remoteJid: payload.remoteJid,
          instanceId: payload.instanceId,
          text: payload.lastMessageTextPreview || payload.text,
          timestamp: payload.timestamp,
          avatar: payload.avatar || payload.profilePicUrl,
        });
      }
    };

    // Listener 3: Chat list updates (trigger quick alert on incoming customer messages)
    const handleChatListUpdate = (payload: any) => {
      if (!payload?.id || payload?.lastMessageFromMe === true || !checkPrivacy(payload)) return;

      handleIncomingMessageAlert({
        chatId: payload.id,
        senderName: payload.name || payload.pushName || payload.remoteJid?.split('@')[0],
        messageText: payload.lastMessageText || payload.text || 'New message received',
        avatarUrl: payload.profilePicUrl || payload.avatar,
        remoteJid: payload.remoteJid,
        instanceId: payload.instanceId,
        soundId: selectedSound,
      });

      if (payload?.isFirstMessage) {
        handleIncomingAlert({
          chatId: payload.id,
          contactId: payload.contactId,
          name: payload.name || payload.pushName,
          remoteJid: payload.remoteJid,
          instanceId: payload.instanceId,
          text: payload.lastMessageText || payload.text,
          timestamp: payload.lastMessageTimestamp || payload.timestamp,
          avatar: payload.profilePicUrl || payload.avatar,
        });
      }
    };

    // Listener 4: Chat claimed by another team member
    const handleChatClaimed = (payload: any) => {
      if (!payload?.chatId && !payload?.contactId) return;

      setAlerts((prev) =>
        prev.map((alert) => {
          if (
            (payload.chatId && alert.chatId === payload.chatId) ||
            (payload.contactId && alert.contactId === payload.contactId)
          ) {
            return {
              ...alert,
              claimedBy: payload.assignedUserName || 'Another team member',
            };
          }
          return alert;
        })
      );

      // Dismiss claimed alert after 2.5 seconds
      setTimeout(() => {
        setAlerts((prev) =>
          prev.filter(
            (alert) =>
              !(
                (payload.chatId && alert.chatId === payload.chatId) ||
                (payload.contactId && alert.contactId === payload.contactId)
              )
          )
        );
      }, 2500);
    };

    // Listener 5: Bidding Started
    const handleBiddingStarted = (payload: any) => {
      if (!payload?.chatId) return;
      handleIncomingAlert({
        chatId: payload.chatId,
        contactId: payload.contactId,
        name: payload.customerName || payload.remoteJid,
        remoteJid: payload.remoteJid,
        instanceId: payload.instanceId,
        text: '⚡ New unassigned chat arrived for Fast Reply / Balloting!',
        timestamp: new Date().toISOString(),
        isBidding: true,
        biddingEndsAt: payload.biddingEndsAt,
        biddingWindowSeconds: payload.biddingWindowSeconds || 10,
        participants: payload.participants || [],
      });
    };

    // Listener 6: Bidding Participant Updated
    const handleBiddingParticipant = (payload: any) => {
      if (!payload?.chatId) return;
      setAlerts((prev) =>
        prev.map((alert) => {
          if (alert.chatId === payload.chatId) {
            const myId = membershipRef.current?.userId;
            const hasMyBid = payload.participants?.some((p: any) => p.userId === myId);
            return {
              ...alert,
              participants: payload.participants,
              myBidSubmitted: hasMyBid,
            };
          }
          return alert;
        })
      );
    };

    // Listener 7: Bidding Ended
    const handleBiddingEnded = (payload: any) => {
      if (!payload?.chatId) return;
      setAlerts((prev) =>
        prev.map((alert) => {
          if (alert.chatId === payload.chatId) {
            const isWinnerMe = payload.winnerUserId === membershipRef.current?.userId;
            return {
              ...alert,
              isBidding: false,
              claimedBy: payload.winnerUserName || (payload.winnerUserId ? 'Winning Agent' : 'No Agents Bid'),
              isWinnerMe,
            };
          }
          return alert;
        })
      );

      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.chatId !== payload.chatId));
      }, 3500);
    };

    channel.bind('new-lead-alert', handleNewLeadAlert);
    channel.bind('new-message', handleNewMessage);
    channel.bind('chat-list-update', handleChatListUpdate);
    channel.bind('chat-claimed', handleChatClaimed);
    channel.bind('chat-bidding-started', handleBiddingStarted);
    channel.bind('chat-bidding-participant', handleBiddingParticipant);
    channel.bind('chat-bidding-ended', handleBiddingEnded);

    return () => {
      channel.unbind('new-lead-alert', handleNewLeadAlert);
      channel.unbind('new-message', handleNewMessage);
      channel.unbind('chat-list-update', handleChatListUpdate);
      channel.unbind('chat-claimed', handleChatClaimed);
      channel.unbind('chat-bidding-started', handleBiddingStarted);
      channel.unbind('chat-bidding-participant', handleBiddingParticipant);
      channel.unbind('chat-bidding-ended', handleBiddingEnded);
    };
  }, [teamId, selectedSound, pathnameWithoutLocale]);

function BiddingCountdownBadge({ endsAt }: { endsAt?: string }) {
  const [remaining, setRemaining] = useState(() => {
    if (!endsAt) return 10;
    return Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 1000));
  });

  useEffect(() => {
    if (!endsAt) return;
    const interval = setInterval(() => {
      const sec = Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 1000));
      setRemaining(sec);
      if (sec <= 0) clearInterval(interval);
    }, 500);
    return () => clearInterval(interval);
  }, [endsAt]);

  return (
    <span className="inline-flex items-center gap-1 font-mono font-bold text-orange-600 dark:text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full text-[11px] border border-orange-500/20">
      <Timer className="size-3 animate-spin" /> {remaining}s left
    </span>
  );
}

  const handleBidNow = async (e: React.MouseEvent, alert: AlertLead) => {
    e.stopPropagation();
    try {
      const res = await fetch(`/api/chats/${alert.chatId}/bid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Your bid has been recorded! Selection will happen when countdown finishes.');
        setAlerts((prev) =>
          prev.map((a) => (a.chatId === alert.chatId ? { ...a, myBidSubmitted: true } : a))
        );
      } else {
        toast.error(data.error || 'Failed to submit bid');
      }
    } catch {
      toast.error('Failed to submit bid');
    }
  };

  // Click handler: Pin bottom dock and start chat immediately without leaving current page!
  const handlePickAndPin = async (alert: AlertLead) => {
    // 1. Immediately open chat in bottom dock
    openChat({
      id: alert.chatId,
      remoteJid: alert.remoteJid,
      name: alert.name,
      avatar: alert.avatar,
      instanceId: alert.instanceId,
    });

    // 2. Cancel dismiss timer and remove toast
    cancelDismiss(alert.chatId);
    setAlerts((prev) => prev.filter((a) => a.chatId !== alert.chatId));

    // 3. If unassigned lead, claim it in the background for this agent
    if (!alert.claimedBy) {
      try {
        const endpoint = alert.contactId
          ? `/api/contacts/${alert.contactId}/pick`
          : `/api/chats/${alert.chatId}/pick`;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            toast.success(`Chat pinned! Handling conversation with ${alert.name}`);
          }
        }
      } catch {
        // Non-blocking
      }
    }
  };

  const handleDismiss = (chatId: number) => {
    cancelDismiss(chatId);
    setAlerts((prev) => prev.filter((a) => a.chatId !== chatId));
  };

  // Do not render anything if no active alerts
  if (alerts.length === 0) return null;

  return (
    <div
      aria-label="New Message Alerts"
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none"
    >
      {alerts.map((alert) => {
        const isClaimed = Boolean(alert.claimedBy);
        const soundObj = NOTIFICATION_SOUNDS.find((s) => s.id === selectedSound);

        return (
          <div
            key={alert.chatId}
            onClick={() => handlePickAndPin(alert)}
            onMouseEnter={() => cancelDismiss(alert.chatId)}
            onMouseLeave={() => scheduleDismiss(alert.chatId, 6000)}
            className={`pointer-events-auto cursor-pointer group rounded-2xl p-4 shadow-2xl border backdrop-blur-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-primary/25 ${
              isClaimed
                ? 'bg-muted/95 border-border text-muted-foreground'
                : alert.isBidding
                  ? 'bg-background/95 dark:bg-[#111b21]/95 border-orange-500/50 dark:border-orange-500/40 ring-1 ring-orange-500/30 shadow-orange-500/15'
                  : 'bg-background/95 dark:bg-[#111b21]/95 border-emerald-500/50 dark:border-emerald-500/40 ring-1 ring-emerald-500/30 shadow-emerald-500/15'
            } animate-in slide-in-from-right-8 fade-in duration-300`}
          >
            {/* Header Row */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span
                    className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                      isClaimed ? 'bg-muted-foreground' : (alert.isBidding ? 'bg-orange-400' : 'bg-emerald-400')
                    }`}
                  />
                  <span
                    className={`relative inline-flex rounded-full h-3 w-3 ${
                      isClaimed ? 'bg-muted-foreground' : (alert.isBidding ? 'bg-orange-500' : 'bg-emerald-500')
                    }`}
                  />
                </span>
                <span className={`font-bold text-xs uppercase tracking-wider flex items-center gap-1 ${
                  alert.isBidding
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-emerald-600 dark:text-emerald-400'
                }`}>
                  {alert.isBidding ? <Flame className="h-3.5 w-3.5 text-orange-500 animate-pulse" /> : <Sparkles className="h-3 w-3" />}
                  {isClaimed ? 'Chat Claimed' : (alert.isBidding ? 'Fast Reply Ballot' : 'Quick Chat Alert')}
                </span>
                {alert.isBidding && !isClaimed && (
                  <BiddingCountdownBadge endsAt={alert.biddingEndsAt} />
                )}
              </div>

              <div className="flex items-center gap-1.5">
                {soundObj && !isClaimed && (
                  <span
                    title={`Ringtone: ${soundObj.name}`}
                    className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/80 bg-muted/60 px-2 py-0.5 rounded-full border border-border/40"
                  >
                    <Volume2 className="h-2.5 w-2.5 text-emerald-500" />
                    <span>{soundObj.name}</span>
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(alert.chatId);
                  }}
                  className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-muted transition-colors"
                  title="Dismiss alert"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Sender & Message Details */}
            <div className="mt-2.5 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 font-semibold text-sm text-foreground truncate">
                  <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate">{alert.name}</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0 font-mono">
                  <Phone className="h-3 w-3" />
                  <span>{alert.remoteJid.split('@')[0]}</span>
                </div>
              </div>

              <div className="bg-[#f0f2f5] dark:bg-[#202c33] p-2.5 rounded-xl border border-border/50 mt-1 shadow-xs">
                <p className="text-xs text-[#111b21] dark:text-gray-200 line-clamp-2 italic font-normal">
                  &ldquo;{alert.text}&rdquo;
                </p>
              </div>
            </div>

            {/* Action Bar */}
            <div className="mt-3.5 flex items-center justify-between gap-2 pt-2 border-t border-border/50">
              {isClaimed ? (
                <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Claimed by {alert.claimedBy}</span>
                </div>
              ) : alert.isBidding ? (
                <div className="w-full flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Flame className="size-3 text-orange-500" />
                    <span>{alert.participants?.length || 0} entered</span>
                  </div>

                  {alert.myBidSubmitted ? (
                    <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
                      <CheckCircle2 className="size-3" /> Bid Entered!
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={(e) => handleBidNow(e, alert)}
                      className="h-8 px-3.5 text-xs font-semibold bg-orange-600 hover:bg-orange-700 text-white shadow-md gap-1.5 rounded-full transition-all cursor-pointer"
                    >
                      <Flame className="size-3.5 text-yellow-300" />
                      Enter Ballot / Accept
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Pin className="h-3 w-3 text-emerald-500" />
                    Click to pin bottom
                  </span>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePickAndPin(alert);
                    }}
                    disabled={alert.claiming}
                    className="h-8 px-3.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md gap-1.5 rounded-full transition-all"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Pin & Start Chat
                  </Button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
